import { ADVANCED_TARGETING_QUALIFICATION_CODE_MIN } from '../../constants/survey-qualification.constants';
import { AdvancedTargetingDefinition } from '../../models/advanced-targeting-definition.model';
import { SurveyQualification } from '../../models/survey-qualification.model';
import { SurveyQualificationTab } from '../../models/survey-qualification-option.model';
import { Quota } from '../../models/survey-quota.model';

// Inverse of StandardSurveyPayloadMapper._advancedTargetingQualification, and a
// port of the legacy populateAdvanceQualifications. Child/dependent definitions
// are deliberately not grouped here — each stands on its own card for now.
export const surveyToAdvancedTargetingDefinitions = (
  surveyQualifications: readonly SurveyQualification[] = [],
  referenceQualifications: readonly SurveyQualificationTab[] = [],
  quotas: readonly Quota[] = [],
): AdvancedTargetingDefinition[] => {
  const referenceCodes = new Set(
    referenceQualifications.map(
      (qualification) => qualification.qualificationCode,
    ),
  );

  return surveyQualifications
    .filter(
      (qualification) =>
        qualification.qualificationCode >=
          ADVANCED_TARGETING_QUALIFICATION_CODE_MIN &&
        // An Advanced Targeting question can also be set up as a front-view or
        // location qualification, in which case it belongs to that section and
        // must not appear here as well.
        !referenceCodes.has(qualification.qualificationCode),
    )
    .map((qualification) => toDefinition(qualification, quotas));
};

const toDefinition = (
  qualification: SurveyQualification,
  quotas: readonly Quota[],
): AdvancedTargetingDefinition => {
  const base = {
    attributeId: String(qualification.qualificationCode),
    title: qualification.qualificationName,
    hasQuotasApplied: qualification.isQuotaSet,
  };

  const definition = qualification.isRangeType
    ? {
        ...base,
        selectedOptionIds: [],
        rangeSet: qualification.rangeSets[0],
      }
    : // `data` carries the answers the survey actually has selected, each with
      // its display text; legacy reads the selection from here rather than
      // from condition_codes.
      {
        ...base,
        selectedOptionIds: qualification.data.map((answer) => answer.id),
      };

  const appliedQuotas = quotasForQualification(qualification, quotas);

  return appliedQuotas.length
    ? { ...definition, quotas: appliedQuotas }
    : definition;
};

const quotasForQualification = (
  qualification: SurveyQualification,
  quotas: readonly Quota[],
): Quota[] =>
  quotas.filter((quota) =>
    quota.criteria.some(
      (criterion) =>
        criterion.qualificationCode === qualification.qualificationCode,
    ),
  );
